import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { useEffect, useState } from "react";
import baseURL from "../../authComponents/baseURL/BaseURL";
import loginToken from "../../authComponents/localStorageItems/LoginTokin";
import * as XLSX from "xlsx";
import { FaEdit, FaEye } from 'react-icons/fa';
import { ToastContainer, toast } from "react-toastify";

const UsersTable = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workerRunning, setWorkerRunning] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await baseURL.get("/admin/users", {
          headers: getAuthHeader()
        });
        if (response.data && Array.isArray(response.data.data)) {
          const mappedUsers = response.data.data.map((user) => ({
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            mobile: user.mobile,
            mobileNumber: user.mobile_number,
            refferal: user.referred_by,
            status: user.status,
            dob: user.dob,
            title: user.title,
            gender: user.gender,
            maritalStatus: user.marital_status,
            caste: user.caste,
            look: user.look,
            education: user.education,
            occupation: user.occupation,
            income: user.income,
            religion: user.religion,
            whatsapp: user.whatsapp,
            youAre: user.you_are,
            isFeatured: user.is_featured,
            age: user.age,
            status: user.status,
            country: user.country,
            division: user.division,
            city: user.city,
          }));
          setUsers(mappedUsers);
          setLoading(false);
        } else {
          console.error("Unexpected response format:", response.data);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.error("Failed to fetch users:", error);
      }
    };

    fetchUsers();
  }, []);

  // Handle user status change
  const handleStatusChange = async (userId, newStatus) => {
    if (newStatus.toLowerCase() === 'rejected') {
      Swal.fire({
        title: 'Please enter Rejection reasons',
        input: 'textarea',
        inputPlaceholder: 'Title not correct...',
        inputAttributes: {
          'aria-label': 'Rejection reason'
        },
        customClass: {
          title: 'swal-title-arial',
          input: 'swal-input-arial',
          popup: 'swal-popup-arial'
        },
        showCancelButton: true,
        confirmButtonText: 'Confirm Rejection',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed && result.value && result.value.trim() !== '') {
          const rejectionReason = result.value;
          updateUserStatus(userId, newStatus, rejectionReason)
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Rejection Failed',
            text: 'Rejection reasons are required for rejection of account.',
            confirmButtonText: 'Okay',
            customClass: {
              title: 'swal-title-arial',
              popup: 'swal-popup-arial'
            }
          });
        }
      });
    } else {
      updateUserStatus(userId, newStatus)
    }
  };

  const updateUserStatus = async (userId, newStatus, rejectionReason = null) => {
    try {
      setWorkerRunning(true);
      const respone = await baseURL.post(
        "/admin/users/update_status",
        { id: userId, status: newStatus, rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${loginToken}` } }
      );
      // Update local state to reflect the new status
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, status: newStatus } : user
        )
      );
      setWorkerRunning(false);
      if (respone.status == 200) {
        toast.success(respone.data.message);
      } else {
        console.error("Failed to update user status:");
      }
    } catch (error) {
      setWorkerRunning(false);
      toast.error(error);
      console.error("Failed to update user status:", error);
    }
  }

  const deleteUser = async (userId) => {
    setWorkerRunning(true);

    try {
      const delete_response = await baseURL.post(
        "/admin/users/delete_user",
        { id: userId },
        { headers: { Authorization: `Bearer ${loginToken}` } }
      );

      if (delete_response.status == 200) {
        Swal.fire(`<h5 style='font-family: "serif"'>User has been Deleted successfully</h5>`, "", "success");
      }
      setWorkerRunning(false);
    } catch (error) {
      setWorkerRunning(false);
      console.error("Failed to delete user:", error);
      Swal.fire(`<h5 style='font-family: "serif"'>Failed to Delete Usery</h5>`, "", "success");
    }
  }

  const handleDeleteUser = (userId) => {
    withReactContent(Swal).fire({
      title: <p style={{ fontFamily: "serif" }}>Are you sure you want to delete this User?</p>,
      showCancelButton: true,
      confirmButtonText: "Delete"
    }).then((result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        deleteUser(userId);
      }
    });
  };

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`
      .trim()
      .toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase().trim());
    const matchesStatus =
      statusFilter === "All" || user.status?.toLowerCase() == statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const formatField = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value;
  };

  // Function to export user data to Excel
  const exportToExcel = () => {
    setIsExporting(true);
    try {
      // Create a worksheet
      const worksheet = XLSX.utils.json_to_sheet(
        filteredUsers.map((user) => ({
          ID: user.id,
          "First Name": user.first_name || "-",
          "Last Name": user.last_name || "-",
          Email: user.email || "-",
          Phone: user.mobile || user.mobileNumber || "-",
          Referral: formatField(user.refferal),
          Age: formatField(user.age),
          "Date of Birth": formatField(user.dob),
          Gender: formatField(user.gender),
          "Marital Status": formatField(user.maritalStatus),
          Religion: formatField(user.religion),
          Caste: formatField(user.caste),
          Look: formatField(user.look),
          Education: formatField(user.education),
          Occupation: formatField(user.occupation),
          Income: formatField(user.income),
          WhatsApp: formatField(user.whatsapp),
          "You Are": formatField(user.youAre),
          Featured: formatField(user.isFeatured),
          Country: formatField(user.country),
          Division: formatField(user.division),
          City: formatField(user.city),
          Status: formatField(user.status),
          Title: formatField(user.title),
        }))
      );

      // Set column widths
      const columnWidths = [
        { wch: 5 }, // ID
        { wch: 15 }, // First Name
        { wch: 15 }, // Last Name
        { wch: 25 }, // Email
        { wch: 15 }, // Phone
        { wch: 5 }, // Age
        { wch: 12 }, // DOB
        { wch: 10 }, // Gender
        { wch: 15 }, // Marital Status
        { wch: 12 }, // Religion
        { wch: 12 }, // Caste
        { wch: 10 }, // Look
        { wch: 20 }, // Education
        { wch: 20 }, // Occupation
        { wch: 10 }, // Income
        { wch: 15 }, // WhatsApp
        { wch: 15 }, // You Are
        { wch: 10 }, // Featured
        { wch: 15 }, // Country
        { wch: 15 }, // Division
        { wch: 15 }, // City
        { wch: 10 }, // Status
        { wch: 30 }, // Title
      ];
      worksheet["!cols"] = columnWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

      // Generate file name with current date and time
      const date = new Date();
      const fileName = `Users_${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}_${date
          .getHours()
          .toString()
          .padStart(2, "0")}-${date
            .getMinutes()
            .toString()
            .padStart(2, "0")}.xlsx`;

      // Save the file
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Failed to export to Excel:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#730000] mx-auto"></div>
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  // Changed condition: Now renders the table even when filteredUsers is empty
  if (!loading) {
    return (
      <>
      <ToastContainer />
        {workerRunning ?
          <div className="overlay">
            <div className="overlay__inner">
              <div className="overlay__content"><span className="spinner"></span></div>
            </div>
          </div> : null}
        <div className="p-6 max-md:p-2 bg-gray-100 min-h-screen font-serif">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold font-serif">Users</h1>
            <button
              onClick={exportToExcel}
              disabled={isExporting || filteredUsers.length === 0}
              className={`px-4 py-2 rounded-md shadow-sm ${isExporting || filteredUsers.length === 0
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
                } transition-colors duration-300 flex items-center`}
            >
              {isExporting ? (
                "Downloading..."
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download Users Data
                </>
              )}
            </button>
          </div>

          <div className="mb-4 flex justify-between">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-52 max-md:w-32 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-serif"
            />

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-serif"
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Active">Active</option>
              <option value="Rejected">Rejected</option>
              <option value="resubmitted">Resubmitted</option>
              <option value="Suspended">Suspended</option>
            </select>
          </div>

          <div className="overflow-hidden overflow-x-auto rounded-lg shadow-md font-serif">
            <table className="min-w-full bg-white table-fixed">
              <thead className="bg-[#730000]">
                <tr className="font-bold text-white">
                  <th className="py-3 px-4 text-left text-sm font-serif">ID</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Controlls</th>
                  <th className="py-3 px-4 text-left text-sm font-serif flex-nowrap min-w-6 truncate">
                    FIRST NAME
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif min-w-6 truncate">
                    LAST NAME
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Email</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Phone</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Referral</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Age</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">DOB</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Gender</th>
                  <th className="py-3 px-4 text-left text-sm font-serif min-w-6 truncate">
                    Marital Status
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">
                    Religion
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Caste</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Look</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">
                    Education
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">
                    Occupation
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">Income</th>
                  <th className="py-3 px-4 text-left text-sm font-serif">
                    WhatsApp
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif min-w-6 truncate">
                    You Are
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">
                    Featured
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">
                    Country
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">
                    Division
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif">City</th>
                  <th className="py-3 px-4 text-center text-sm font-serif">
                    Action
                  </th>
                  <th className="py-3 px-4 text-center text-sm font-serif">
                    Delete
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-serif ">Title</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="odd:bg-white even:bg-slate-100 border-b font-serif"
                  >
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {user.id}
                    </td>
                    <td className="py-3 px-4 flex row items-center gap-4 text-sm text-gray-800 font-serif">
                      <a href={`/edit-profile?act_as_admin_for_user=${user.id}`} target="_blank" className='bg-yellow-600 text-white px-2 py-2'>
                        <FaEdit />
                      </a>
                      <a href={`/profile-details/${user.id}`} target="_blank" className='bg-blue-600 text-white px-2 py-2'>
                        <FaEye />
                      </a>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 whitespace-nowrap font-serif">
                      {user.first_name}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 whitespace-nowrap font-serif">
                      {user.last_name}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {user.email}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {user.mobile || user.mobileNumber || "-"}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.refferal)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.age)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif min-w-8 truncate">
                      {formatField(user.dob)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.gender)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.maritalStatus)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.religion)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.caste)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.look)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.education)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.occupation)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.income)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.whatsapp)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.youAre)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.isFeatured)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.country)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.division)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif">
                      {formatField(user.city)}
                    </td>
                    {/* Status Action Dropdown */}
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif align-middle">
                      <select
                        className="border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full min-w-[90px]"
                        onChange={(e) =>
                          handleStatusChange(user.id, e.target.value)
                        }
                        value={user.status}
                      >
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="rejected">Rejected</option>
                        <option value="resubmitted">Resubmitted</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif align-middle">
                      <button
                        className="px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors duration-200 focus:outline-none w-full max-w-[120px]"
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        Delete
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-800 font-serif  min-w-[20vw] truncate">
                      {formatField(user.title)}
                    </td>
                  </tr>
                ))}

                {/* Show message if no users match filters */}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td
                      colSpan="26" // Updated column span to match total number of columns
                      className="py-8 px-4 text-center text-sm text-gray-500 font-serif"
                    >
                      No users found matching the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

};

export default UsersTable;
